import { useState } from "react";

export default function DepositDetailsModal({ deposit, onClose, isDarkMode, isMobile }) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const dark = isDarkMode;

  if (!deposit) return null;

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "#4CAF50";
      case "pending":
        return "#FF9800";
      case "processing":
        return "#2196F3";
      case "failed":
        return "#F44336";
      default:
        return "#666";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed":
        return "✓";
      case "pending":
        return "⏳";
      case "processing":
        return "⚡";
      case "failed":
        return "✗";
      default:
        return "•";
    }
  };

  const styles = {
    overlay: {
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.7)",
      backdropFilter: "blur(8px)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 2000,
      animation: "fadeIn 0.3s ease-out",
      padding: isMobile ? "1rem" : "2rem",
    },
    modal: {
      width: "100%",
      maxWidth: isMobile ? "100%" : 600,
      maxHeight: "90vh",
      overflowY: "auto",
      background: dark
        ? "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)"
        : "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
      borderRadius: 24,
      boxShadow: "0 25px 70px rgba(0,0,0,0.4)",
      animation: "slideUp 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
    },
    header: {
      padding: isMobile ? "1.5rem" : "2rem",
      borderBottom: `1px solid ${dark ? "#333" : "#e0e0e0"}`,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      position: "sticky",
      top: 0,
      background: dark
        ? "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)"
        : "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
      zIndex: 10,
    },
    title: {
      fontSize: isMobile ? 20 : 24,
      fontWeight: 700,
      color: dark ? "#f0f0f0" : "#333",
      display: "flex",
      alignItems: "center",
      gap: "0.75rem",
    },
    closeButton: {
      width: 40,
      height: 40,
      borderRadius: "50%",
      background: dark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)",
      border: "none",
      color: dark ? "#f0f0f0" : "#333",
      fontSize: 20,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      transition: "all 0.3s",
    },
    content: {
      padding: isMobile ? "1.5rem" : "2rem",
    },
    statusBanner: {
      padding: "1rem",
      borderRadius: 12,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "0.5rem",
      marginBottom: "2rem",
      fontSize: 16,
      fontWeight: 600,
      textTransform: "uppercase",
      letterSpacing: "0.05em",
    },
    infoGrid: {
      display: "grid",
      gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
      gap: "1.5rem",
      marginBottom: "2rem",
    },
    infoCard: {
      padding: "1rem",
      background: dark ? "#111" : "#f8f9fa",
      borderRadius: 12,
      border: `1px solid ${dark ? "#333" : "#e0e0e0"}`,
    },
    infoLabel: {
      fontSize: 12,
      fontWeight: 600,
      color: dark ? "#888" : "#666",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      marginBottom: "0.5rem",
      display: "flex",
      alignItems: "center",
      gap: "0.25rem",
    },
    infoValue: {
      fontSize: isMobile ? 16 : 18,
      fontWeight: 600,
      color: dark ? "#f0f0f0" : "#333",
      wordBreak: "break-all",
    },
    screenshotSection: {
      marginBottom: "2rem",
    },
    sectionTitle: {
      fontSize: 16,
      fontWeight: 600,
      color: dark ? "#f0f0f0" : "#333",
      marginBottom: "1rem",
      display: "flex",
      alignItems: "center",
      gap: "0.5rem",
    },
    screenshotContainer: {
      position: "relative",
      background: dark ? "#000" : "#f0f0f0",
      borderRadius: 12,
      overflow: "hidden",
      border: `1px solid ${dark ? "#333" : "#e0e0e0"}`,
      minHeight: 200,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    screenshot: {
      width: "100%",
      height: "auto",
      display: "block",
      maxHeight: 400,
      objectFit: "contain",
    },
    loadingSpinner: {
      width: 40,
      height: 40,
      border: `3px solid ${dark ? "#333" : "#e0e0e0"}`,
      borderTop: "3px solid #667eea",
      borderRadius: "50%",
      animation: "spin 1s linear infinite",
    },
    timeline: {
      marginBottom: "2rem",
    },
    timelineItem: {
      display: "flex",
      gap: "1rem",
      marginBottom: "1.5rem",
      position: "relative",
    },
    timelineDot: {
      width: 12,
      height: 12,
      borderRadius: "50%",
      background: "#667eea",
      marginTop: 5,
      flexShrink: 0,
      position: "relative",
      zIndex: 2,
    },
    timelineLine: {
      position: "absolute",
      left: 5,
      top: 20,
      bottom: -20,
      width: 2,
      background: dark ? "#333" : "#e0e0e0",
      zIndex: 1,
    },
    timelineContent: {
      flex: 1,
    },
    timelineDate: {
      fontSize: 12,
      color: dark ? "#888" : "#666",
      marginBottom: "0.25rem",
    },
    timelineTitle: {
      fontSize: 14,
      fontWeight: 600,
      color: dark ? "#f0f0f0" : "#333",
    },
    actionButtons: {
      display: "flex",
      gap: "1rem",
      flexWrap: "wrap",
    },
    actionButton: {
      flex: 1,
      minWidth: isMobile ? "100%" : 150,
      padding: "0.75rem 1.5rem",
      borderRadius: 10,
      border: "none",
      fontSize: 14,
      fontWeight: 600,
      cursor: "pointer",
      transition: "all 0.3s",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "0.5rem",
    },
    primaryButton: {
      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      color: "#fff",
      boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
    },
    secondaryButton: {
      background: dark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)",
      color: dark ? "#f0f0f0" : "#333",
      border: `1px solid ${dark ? "#333" : "#e0e0e0"}`,
    },
    note: {
      padding: "1rem",
      background: dark ? "rgba(255, 193, 7, 0.1)" : "rgba(255, 193, 7, 0.05)",
      border: `1px solid ${dark ? "rgba(255, 193, 7, 0.3)" : "rgba(255, 193, 7, 0.2)"}`,
      borderRadius: 10,
      marginTop: "1.5rem",
      fontSize: 13,
      color: dark ? "#ccc" : "#666",
      lineHeight: 1.5,
    },
  };

  const timeline = [
    {
      date: deposit.date,
      title: "Deposit initiated",
      active: true,
    },
    {
      date: "2024-01-15 10:35 AM",
      title: "Payment received",
      active: deposit.status !== "failed",
    },
    {
      date: "2024-01-15 10:40 AM",
      title: "Under verification",
      active: deposit.status === "processing" || deposit.status === "completed",
    },
    {
      date: "2024-01-15 10:45 AM",
      title: deposit.status === "completed" ? "Completed" : "Pending completion",
      active: deposit.status === "completed",
    },
  ];

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.header}>
          <h2 style={styles.title}>
            <span>📄</span>
            Deposit Details
          </h2>
          <button
            style={styles.closeButton}
            onClick={onClose}
            onMouseEnter={(e) => {
              e.target.style.background = dark
                ? "rgba(255,255,255,0.2)"
                : "rgba(0,0,0,0.1)";
            }}
            onMouseLeave={(e) => {
              e.target.style.background = dark
                ? "rgba(255,255,255,0.1)"
                : "rgba(0,0,0,0.05)";
            }}
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div style={styles.content}>
          {/* Status Banner */}
          <div
            style={{
              ...styles.statusBanner,
              background: `${getStatusColor(deposit.status)}20`,
              color: getStatusColor(deposit.status),
            }}
          >
            <span style={{ fontSize: 20 }}>{getStatusIcon(deposit.status)}</span>
            <span>Payment {deposit.status}</span>
          </div>

          {/* Info Grid */}
          <div style={styles.infoGrid}>
            <div style={styles.infoCard}>
              <div style={styles.infoLabel}>
                <span>💰</span>
                Amount
              </div>
              <div style={styles.infoValue}>₹{deposit.amount.toLocaleString()}</div>
            </div>
            <div style={styles.infoCard}>
              <div style={styles.infoLabel}>
                <span>🔢</span>
                Transaction ID
              </div>
              <div style={styles.infoValue}>{deposit.id}</div>
            </div>
            <div style={styles.infoCard}>
              <div style={styles.infoLabel}>
                <span>💳</span>
                Payment Method
              </div>
              <div style={styles.infoValue}>{deposit.method}</div>
            </div>
            <div style={styles.infoCard}>
              <div style={styles.infoLabel}>
                <span>🆔</span>
                Reference Number
              </div>
              <div style={styles.infoValue}>{deposit.transactionId}</div>
            </div>
          </div>

          {/* Screenshot Section */}
          <div style={styles.screenshotSection}>
            <h3 style={styles.sectionTitle}>
              <span>📸</span>
              Payment Screenshot
            </h3>
            <div style={styles.screenshotContainer}>
              {!imageLoaded && <div style={styles.loadingSpinner}></div>}
              {/* Placeholder for screenshot - in real app, this would be the actual image */}
              <div
                style={{
                  width: "100%",
                  height: 300,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: dark ? "#666" : "#999",
                  fontSize: 14,
                }}
              >
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 48, marginBottom: "1rem" }}>🖼️</div>
                  Payment screenshot will appear here
                  <br />
                  <small>({deposit.screenshot})</small>
                </div>
              </div>
            </div>
          </div>

          {/* Timeline */}
          <div style={styles.timeline}>
            <h3 style={styles.sectionTitle}>
              <span>⏱️</span>
              Transaction Timeline
            </h3>
            {timeline.map((item, index) => (
              <div key={index} style={styles.timelineItem}>
                <div
                  style={{
                    ...styles.timelineDot,
                    background: item.active ? "#667eea" : dark ? "#444" : "#ddd",
                  }}
                />
                {index < timeline.length - 1 && <div style={styles.timelineLine} />}
                <div style={styles.timelineContent}>
                  <div style={styles.timelineDate}>{item.date}</div>
                  <div
                    style={{
                      ...styles.timelineTitle,
                      opacity: item.active ? 1 : 0.5,
                    }}
                  >
                    {item.title}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Action Buttons */}
          <div style={styles.actionButtons}>
            <button
              style={{ ...styles.actionButton, ...styles.primaryButton }}
              onMouseEnter={(e) => {
                e.target.style.transform = "translateY(-2px)";
                e.target.style.boxShadow = "0 6px 20px rgba(102, 126, 234, 0.4)";
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = "translateY(0)";
                e.target.style.boxShadow = "0 4px 12px rgba(102, 126, 234, 0.3)";
              }}
            >
              <span>💬</span>
              Contact Support
            </button>
            <button
              style={{ ...styles.actionButton, ...styles.secondaryButton }}
              onMouseEnter={(e) => {
                e.target.style.background = dark
                  ? "rgba(255,255,255,0.15)"
                  : "rgba(0,0,0,0.08)";
              }}
              onMouseLeave={(e) => {
                e.target.style.background = dark
                  ? "rgba(255,255,255,0.1)"
                  : "rgba(0,0,0,0.05)";
              }}
            >
              <span>⬇️</span>
              Download Receipt
            </button>
          </div>

          {/* Note */}
          <div style={styles.note}>
            <strong>Note:</strong> If you have any questions about this transaction,
            please contact our support team with the transaction ID. Deposits are typically
            processed within 2-4 hours during business hours.
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}